// client/src/pages/admin/project-steps/Step2Location.tsx
import React, { useState, useEffect, useRef } from 'react';
import { StepProps } from './types';
import MapPicker from './MapPicker';

interface Step2Props extends StepProps {
    cities: { _id: string, name: string }[];
    filteredLocalities: { _id: string, name: string }[];
    packageFeatures?: any;
}

const Step2Location: React.FC<Step2Props> = ({ form, setForm, handleChange, cities, filteredLocalities, errors, QuickUpdateButton, packageFeatures }) => {
    const [citySearch, setCitySearch] = useState('');
    const [isCityOpen, setIsCityOpen] = useState(false);
    const [localitySearch, setLocalitySearch] = useState('');
    const [isLocalityOpen, setIsLocalityOpen] = useState(false);
    const hasGoogleMap = packageFeatures === undefined ? true : !!packageFeatures?.googleMap;
    const hasStreetView = packageFeatures === undefined ? true : !!packageFeatures?.streetView;

    const cityRef = useRef<HTMLDivElement>(null);
    const localityRef = useRef<HTMLDivElement>(null);

    const filteredCities = cities.filter(c =>
        c.name.toLowerCase().includes(citySearch.toLowerCase())
    );

    const displayedLocalities = filteredLocalities.filter(l =>
        l.name.toLowerCase().includes(localitySearch.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (cityRef.current && !cityRef.current.contains(event.target as Node)) setIsCityOpen(false);
            if (localityRef.current && !localityRef.current.contains(event.target as Node)) setIsLocalityOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectCity = (cityName: string) => {
        handleChange({ target: { name: 'city', value: cityName } } as any);
        setIsCityOpen(false);
        setCitySearch('');
    };

    const handleSelectLocality = (localityName: string) => {
        handleChange({ target: { name: 'location', value: localityName } } as any);
        setIsLocalityOpen(false);
        setLocalitySearch('');
    };

    // Called when user clicks on the map
    const handleLocationPick = (lat: number, lng: number) => {
        const googleUrl = `https://maps.google.com/?q=${lat.toFixed(6)},${lng.toFixed(6)}`;
        if (setForm) {
            setForm(prev => ({
                ...prev,
                latitude: lat.toFixed(6),
                longitude: lng.toFixed(6),
                googleMapLocation: googleUrl
            }));
        } else {
            handleChange({ target: { name: 'latitude', value: lat.toFixed(6) } } as any);
            handleChange({ target: { name: 'longitude', value: lng.toFixed(6) } } as any);
            handleChange({ target: { name: 'googleMapLocation', value: googleUrl } } as any);
        }
    };

    const mapViewUrl = form.googleMapLocation || (form.latitude && form.longitude
        ? `https://maps.google.com/?q=${form.latitude},${form.longitude}`
        : `https://maps.google.com/?q=${encodeURIComponent(form.name || 'Ahmedabad')}`);

    return (
        <div className="space-y-0">
            {/* Header */}
            <div className="flex items-center gap-3 mb-5 px-1">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg shadow-md">
                    2
                </div>
                <div>
                    <h5 className="text-xl font-bold text-gray-800">Location Details</h5>
                    <p className="text-xs text-gray-500">Add project location and map details</p>
                </div>
            </div>

            {/* Single-column layout */}
            <div className="flex flex-col gap-4">

                {/* ─── LEFT: Form Fields ─── */}
                <div className="space-y-4">

                    {/* Row 1: Full Address | City | Locality */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {/* Full Address */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-600 block">Full Address <span className="text-rose-500">*</span></label>
                            <div className="relative flex items-center">
                                <span className="absolute left-3 text-blue-500">
                                    <i className="fa-solid fa-location-dot text-sm"></i>
                                </span>
                                <input
                                    type="text"
                                    className={`w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border ${errors?.address ? 'border-rose-500 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                                    name="address"
                                    value={form.address}
                                    onChange={handleChange}
                                    placeholder="Enter full project address"
                                />
                            </div>
                        </div>

                        {/* City */}
                        <div className="space-y-1.5" ref={cityRef}>
                            <label className="text-xs font-bold text-gray-600 block">City <span className="text-rose-500">*</span></label>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setIsCityOpen(o => !o)}
                                    className={`w-full flex items-center pl-9 pr-8 py-2.5 rounded-xl bg-gray-50 border text-left font-semibold text-sm transition-all ${
                                        errors?.city
                                            ? 'border-rose-500 bg-rose-50 text-rose-600'
                                            : isCityOpen
                                                ? 'border-blue-500 bg-white text-gray-800'
                                                : 'border-gray-200 text-gray-700'
                                    }`}
                                >
                                    <span className="absolute left-3 text-blue-500">
                                        <i className="fa-solid fa-city text-sm"></i>
                                    </span>
                                    <span className="truncate flex-1">
                                        {form.city || <span className="text-gray-400 font-normal">Enter city</span>}
                                    </span>
                                    <span className="absolute right-3 text-gray-400">
                                        <i className={`fa-solid fa-chevron-${isCityOpen ? 'up' : 'down'} text-xs`}></i>
                                    </span>
                                </button>
                                {isCityOpen && (
                                    <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                                        <div className="p-2 border-b border-gray-100">
                                            <div className="relative">
                                                <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                                                <input
                                                    autoFocus
                                                    type="text"
                                                    className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-400 text-sm font-semibold"
                                                    placeholder="Search city..."
                                                    value={citySearch}
                                                    onChange={e => setCitySearch(e.target.value)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                        <div className="max-h-52 overflow-y-auto">
                                            {filteredCities.length > 0 ? filteredCities.map(c => (
                                                <div key={c._id}
                                                    className={`px-4 py-2.5 cursor-pointer text-sm font-semibold flex items-center justify-between transition-colors ${form.city === c.name ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                                                    onClick={() => handleSelectCity(c.name)}>
                                                    {c.name}
                                                    {form.city === c.name && <i className="fa-solid fa-check text-blue-600 text-xs"></i>}
                                                </div>
                                            )) : (
                                                <div className="px-4 py-6 text-center text-gray-400 text-xs font-bold">No cities found</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Locality */}
                        <div className="space-y-1.5" ref={localityRef}>
                            <label className="text-xs font-bold text-gray-600 block">Locality <span className="text-rose-500">*</span></label>
                            <div className="relative">
                                <button
                                    type="button"
                                    disabled={!form.city}
                                    onClick={() => form.city && setIsLocalityOpen(o => !o)}
                                    className={`w-full flex items-center pl-9 pr-8 py-2.5 rounded-xl bg-gray-50 border text-left font-semibold text-sm transition-all ${
                                        errors?.location
                                            ? 'border-rose-500 bg-rose-50 text-rose-600'
                                            : !form.city
                                                ? 'border-gray-100 text-gray-300 cursor-not-allowed opacity-60'
                                                : isLocalityOpen
                                                    ? 'border-blue-500 bg-white text-gray-800'
                                                    : 'border-gray-200 text-gray-700'
                                    }`}
                                >
                                    <span className="absolute left-3 text-green-500">
                                        <i className="fa-solid fa-map-pin text-sm"></i>
                                    </span>
                                    <span className="truncate flex-1">
                                        {form.location || <span className="text-gray-400 font-normal">Enter locality</span>}
                                    </span>
                                    <span className="absolute right-3 text-gray-400">
                                        <i className={`fa-solid fa-chevron-${isLocalityOpen ? 'up' : 'down'} text-xs`}></i>
                                    </span>
                                </button>
                                {isLocalityOpen && form.city && (
                                    <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                                        <div className="p-2 border-b border-gray-100">
                                            <div className="relative">
                                                <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                                                <input
                                                    autoFocus
                                                    type="text"
                                                    className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-400 text-sm font-semibold"
                                                    placeholder="Search locality..."
                                                    value={localitySearch}
                                                    onChange={e => setLocalitySearch(e.target.value)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                        <div className="max-h-52 overflow-y-auto">
                                            {displayedLocalities.length > 0 ? displayedLocalities.map(l => (
                                                <div key={l._id}
                                                    className={`px-4 py-2.5 cursor-pointer text-sm font-semibold flex items-center justify-between transition-colors ${form.location === l.name ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                                                    onClick={() => handleSelectLocality(l.name)}>
                                                    {l.name}
                                                    {form.location === l.name && <i className="fa-solid fa-check text-blue-600 text-xs"></i>}
                                                </div>
                                            )) : (
                                                <div className="px-4 py-6 text-center text-gray-400 text-xs font-bold">No localities found</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Row 2: Pin Code | State | Country */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {/* Pin Code */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-600 block">Pin Code <span className="text-rose-500">*</span></label>
                            <div className="relative flex items-center">
                                <span className="absolute left-3 text-orange-500">
                                    <i className="fa-solid fa-hashtag text-sm"></i>
                                </span>
                                <input
                                    type="text"
                                    className={`w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border ${errors?.pinCode ? 'border-rose-500 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                                    name="pinCode"
                                    value={form.pinCode}
                                    onChange={handleChange}
                                    placeholder="Enter pin code"
                                />
                            </div>
                        </div>

                        {/* State */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-600 block">State <span className="text-rose-500">*</span></label>
                            <div className="relative flex items-center">
                                <span className="absolute left-3 text-purple-500">
                                    <i className="fa-solid fa-map text-sm"></i>
                                </span>
                                <select
                                    className={`w-full pl-9 pr-8 py-2.5 rounded-xl bg-gray-50 border ${errors?.state ? 'border-rose-500 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm appearance-none`}
                                    name="state"
                                    value={form.state}
                                    onChange={handleChange}
                                >
                                    <option value="">Select state</option>
                                    {['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh','Puducherry','Chandigarh'].map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                                <span className="absolute right-3 text-gray-400 pointer-events-none">
                                    <i className="fa-solid fa-chevron-down text-xs"></i>
                                </span>
                            </div>
                        </div>

                        {/* Country */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-600 block">Country <span className="text-rose-500">*</span></label>
                            <div className="relative flex items-center">
                                <span className="absolute left-3 text-emerald-500">
                                    <i className="fa-solid fa-globe text-sm"></i>
                                </span>
                                <select
                                    className="w-full pl-9 pr-8 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-800 focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm appearance-none"
                                    name="country"
                                    value={form.country}
                                    onChange={handleChange}
                                >
                                    <option value="India">India</option>
                                    <option value="UAE">UAE</option>
                                    <option value="USA">USA</option>
                                    <option value="UK">UK</option>
                                </select>
                                <span className="absolute right-3 text-gray-400 pointer-events-none">
                                    <i className="fa-solid fa-chevron-down text-xs"></i>
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Row 3: Latitude | Longitude | Landmark */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-600 block">Latitude <span className="text-rose-500">*</span></label>
                            <div className="relative flex items-center">
                                <span className="absolute left-3 text-rose-400">
                                    <i className="fa-solid fa-arrows-up-down text-sm"></i>
                                </span>
                                <input
                                    type="text"
                                    className={`w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border ${errors?.latitude ? 'border-rose-500 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                                    name="latitude"
                                    value={form.latitude}
                                    onChange={handleChange}
                                    placeholder="e.g. 23.0225"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-600 block">Longitude <span className="text-rose-500">*</span></label>
                            <div className="relative flex items-center">
                                <span className="absolute left-3 text-indigo-400">
                                    <i className="fa-solid fa-arrows-left-right text-sm"></i>
                                </span>
                                <input
                                    type="text"
                                    className={`w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border ${errors?.longitude ? 'border-rose-500 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                                    name="longitude"
                                    value={form.longitude}
                                    onChange={handleChange}
                                    placeholder="e.g. 72.5714"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-600 block">Landmark (Optional)</label>
                            <div className="relative flex items-center">
                                <span className="absolute left-3 text-amber-500">
                                    <i className="fa-solid fa-landmark text-sm"></i>
                                </span>
                                <input
                                    type="text"
                                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-800 focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm"
                                    name="landmark"
                                    value={(form as any).landmark || ''}
                                    onChange={handleChange}
                                    placeholder="e.g. Near SG Highway"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Row 4: Google Map URL */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-600 flex items-center justify-between">
                            <span>Google Map URL (Project Name based) <span className="text-rose-500">*</span></span>
                            {!hasGoogleMap && <span className="text-[10px] text-red-500 bg-red-50 px-2 py-0.5 rounded flex items-center gap-1"><i className="fa-solid fa-lock"></i> Package Upgrade Required</span>}
                        </label>
                        <div className="relative flex items-center">
                            <span className="absolute left-3 text-blue-500">
                                <i className="fa-solid fa-map-location-dot text-sm"></i>
                            </span>
                            <input
                                type="text"
                                className={`w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border ${errors?.googleMapLocation ? 'border-rose-500 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm disabled:opacity-60 disabled:cursor-not-allowed`}
                                name="googleMapLocation"
                                value={form.googleMapLocation}
                                onChange={handleChange}
                                placeholder="https://maps.google.com/?q=your-project-name"
                                disabled={!hasGoogleMap}
                            />
                        </div>
                    </div>

                    {/* Google Street View */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-600 flex items-center justify-between">
                            <span>Google Street View Link (Project Location based)</span>
                            {!hasStreetView && <span className="text-[10px] text-red-500 bg-red-50 px-2 py-0.5 rounded flex items-center gap-1"><i className="fa-solid fa-lock"></i> Package Upgrade Required</span>}
                        </label>
                        <div className="relative flex items-center">
                            <span className="absolute left-3 text-teal-500">
                                <i className="fa-solid fa-person-walking text-sm"></i>
                            </span>
                            <input
                                type="text"
                                className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-800 focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                                name="google3dViewLink"
                                value={form.google3dViewLink}
                                onChange={handleChange}
                                placeholder="https://maps.google.com/..."
                                disabled={!hasStreetView}
                            />
                        </div>
                    </div>
                </div>

                {/* ─── Map Preview (below Google Map URL) ─── */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    {/* Map Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <span className="text-sm font-bold text-gray-700 flex items-center gap-2">
                            <i className="fa-solid fa-map text-blue-500 text-xs"></i>
                            Map Preview
                            <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                                <i className="fa-solid fa-hand-pointer text-[9px]"></i>
                                Click on map to pick location
                            </span>
                        </span>
                        <a
                            href={mapViewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1.5 transition-colors"
                        >
                            View on Google Maps
                            <i className="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
                        </a>
                    </div>
                    {/* Interactive Leaflet Map */}
                    <MapPicker
                        latitude={form.latitude}
                        longitude={form.longitude}
                        onLocationPick={handleLocationPick}
                    />
                    {/* Tip inside map card */}
                    <div className="px-4 py-3 bg-blue-50 border-t border-blue-100 flex items-start gap-2">
                        <i className="fa-solid fa-circle-info text-blue-500 text-sm mt-0.5 shrink-0"></i>
                        <p className="text-xs text-blue-700 font-semibold leading-relaxed">
                            Tip: Click anywhere on the map to auto-fill Latitude, Longitude &amp; Google Map URL.
                        </p>
                    </div>
                </div>

                {/* ─── Below Map: Address Showcase ─── */}
                <div className="space-y-4">
                    {/* Address Showcase */}
                    <div className="bg-green-50 border border-gray-200 rounded-xl p-4">
                        <p className="text-xs font-bold text-gray-500 mb-3">Address Showcase (How it will appear on website)</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                            <div className="flex flex-col items-center text-center p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                                <i className="fa-solid fa-location-dot text-blue-500 text-base mb-1"></i>
                                <span className="text-[10px] font-bold text-blue-600 leading-tight truncate w-full text-center">{form.name || 'Project Name'}</span>
                                <span className="text-[9px] text-gray-400 mt-0.5">Project Name</span>
                            </div>
                            <div className="flex flex-col items-center text-center p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                                <i className="fa-solid fa-map-pin text-blue-500 text-base mb-1"></i>
                                <span className="text-[10px] font-bold text-blue-600 leading-tight truncate w-full text-center">{form.location && form.city ? `${form.location}, ${form.city}` : 'Location'}</span>
                                <span className="text-[9px] text-gray-400 mt-0.5">Location</span>
                            </div>
                            <div className="flex flex-col items-center text-center p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                                <i className="fa-solid fa-hashtag text-blue-500 text-base mb-1"></i>
                                <span className="text-[10px] font-bold text-blue-600 leading-tight truncate w-full text-center">{form.pinCode || '382470'}</span>
                                <span className="text-[9px] text-gray-400 mt-0.5">Pin Code</span>
                            </div>
                            <div className="flex flex-col items-center text-center p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                                <i className="fa-solid fa-building text-blue-500 text-base mb-1"></i>
                                <span className="text-[10px] font-bold text-blue-600 leading-tight truncate w-full text-center">{form.city || 'Ahmedabad'}</span>
                                <span className="text-[9px] text-gray-400 mt-0.5">City</span>
                            </div>
                            <div className="flex flex-col items-center text-center p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                                <i className="fa-solid fa-map text-blue-500 text-base mb-1"></i>
                                <span className="text-[10px] font-bold text-blue-600 leading-tight truncate w-full text-center">{form.googleMapLocation ? 'View on Map' : 'Not Set'}</span>
                                <span className="text-[9px] text-gray-400 mt-0.5">Google Maps</span>
                            </div>
                            <div className="flex flex-col items-center text-center p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                                <i className="fa-solid fa-person-walking text-blue-500 text-base mb-1"></i>
                                <span className="text-[10px] font-bold text-blue-600 leading-tight truncate w-full text-center">{form.google3dViewLink ? 'Street View' : 'Not Set'}</span>
                                <span className="text-[9px] text-gray-400 mt-0.5">Google Street View</span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Step2Location;
